import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { AlertTriangle, CheckCircle2, Loader, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { EMAIL_VERIFICATION_STATE } from '@/lib/constants';
import { authClient, useOptionalSession } from '@/providers/session';

export const VerifyEmailTokenPage = () => {
  useLocale();

  const { refreshSession } = useOptionalSession();
  const navigate = useNavigate();
  const params = useParams();
  const [state, setState] = useState<keyof typeof EMAIL_VERIFICATION_STATE | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = async (err?: unknown) => {
    console.error(err);
    toast.error(t('auth.somethingWentWrong'), {
      get description() {
        return t('auth.weWereUnableToVerifyYourEmailAtThis');
      },
    });

    await navigate('/auth/verify-email');
    setIsLoading(false);
  };
  const verifyToken = async () => {
    setIsLoading(true);

    try {
      if (!params.token) {
        return await handleError();
      }
      const response = await authClient.emailPassword.verifyEmail({
        token: params.token,
      });
      await refreshSession();
      setState(response.state);
      setIsLoading(false);
    } catch (err) {
      await handleError(err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void verifyToken();
  }, []);

  if (isLoading || state === null) {
    return (
      <div className="relative">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return match(state)
    .with(EMAIL_VERIFICATION_STATE.NOT_FOUND, () => (
      <div className="w-screen max-w-lg px-4">
        <div className="flex w-full items-start">
          <div className="me-4 mt-1 hidden md:block">
            <AlertTriangle className="h-10 w-10 text-yellow-500" strokeWidth={2} />
          </div>

          <div>
            <h2 className="font-bold text-2xl md:text-4xl">{t('auth.somethingWentWrong')}</h2>

            <p className="mt-4 text-muted-foreground">
              {t('auth.weWereUnableToVerifyYourEmailIfYour')}{' '}
            </p>

            <Button asChild className="mt-4">
              <Link to="/">{t('auth.goBackHome')}</Link>
            </Button>
          </div>
        </div>
      </div>
    ))
    .with(EMAIL_VERIFICATION_STATE.EXPIRED, () => (
      <div className="w-screen max-w-lg px-4">
        <div className="flex w-full items-start">
          <div className="me-4 mt-1 hidden md:block">
            <XCircle className="h-10 w-10 text-destructive" strokeWidth={2} />
          </div>

          <div>
            <h2 className="font-bold text-2xl md:text-4xl">{t('auth.yourTokenHasExpired')}</h2>

            <p className="mt-4 text-muted-foreground">
              {t('auth.itSeemsThatTheProvidedTokenHasExpiredWe')}{' '}
            </p>

            <Button asChild className="mt-4">
              <Link to="/">{t('auth.goBackHome')}</Link>
            </Button>
          </div>
        </div>
      </div>
    ))
    .with(EMAIL_VERIFICATION_STATE.VERIFIED, () => (
      <div className="w-screen max-w-lg px-4">
        <div className="flex w-full items-start">
          <div className="me-4 mt-1 hidden md:block">
            <CheckCircle2 className="h-10 w-10 text-green-500" strokeWidth={2} />
          </div>

          <div>
            <h2 className="font-bold text-2xl md:text-4xl">{t('auth.emailConfirmed')}</h2>

            <p className="mt-4 text-muted-foreground">
              {t('auth.yourEmailHasBeenSuccessfullyConfirmedYouCanNow')}{' '}
            </p>

            <Button asChild className="mt-4">
              <Link to="/o">{t('auth.continue')}</Link>
            </Button>
          </div>
        </div>
      </div>
    ))
    .with(EMAIL_VERIFICATION_STATE.ALREADY_VERIFIED, () => (
      <div className="w-screen max-w-lg px-4">
        <div className="flex w-full items-start">
          <div className="me-4 mt-1 hidden md:block">
            <CheckCircle2 className="h-10 w-10 text-green-500" strokeWidth={2} />
          </div>

          <div>
            <h2 className="font-bold text-2xl md:text-4xl">{t('auth.emailAlreadyConfirmed')}</h2>

            <p className="mt-4 text-muted-foreground">
              {t('auth.yourEmailHasAlreadyBeenConfirmedYouCanNow')}{' '}
            </p>

            <Button asChild className="mt-4">
              <Link to="/auth/signin">{t('auth.goBackHome')}</Link>
            </Button>
          </div>
        </div>
      </div>
    ))
    .exhaustive();
};
